// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// packages/react-css/src/lib/analyticsCopy.ts

/**
 * Geenius Analytics React CSS - Analytics Copy
 *
 * @module ReactCssAnalyticsCopy
 * @description Provides localized analytics UI copy helpers for the vanilla CSS
 * React variant, using an optional provider-supplied translator and shared
 * translation fallbacks.
 */

import {
  formatAnalyticsTime,
  getAnalyticsDeviceLabel,
  pluralizeAnalytics,
  translateAnalytics,
} from "@geenius/analytics";
import { useCallback } from "react";
import { useAnalyticsCopyTranslator } from "../provider";

type AnalyticsParams = Record<string, string | number>;

function buildPluralKey(key: string, count: number): string {
  if (count === 0) {
    return `${key}_zero`;
  }

  if (count === 1) {
    return `${key}_one`;
  }

  return `${key}_other`;
}

export function useAnalyticsCopy(): {
  t: (key: string, params?: AnalyticsParams) => string;
  fallback: (key: string, fallback: string, params?: AnalyticsParams) => string;
  plural: (key: string, count: number, params?: AnalyticsParams) => string;
  deviceLabel: (device: string) => string;
  formatTime: (value: number | string, locale?: string) => string;
} {
  const providerTranslator = useAnalyticsCopyTranslator();

  const translateWithProvider = useCallback(
    (key: string, params?: AnalyticsParams): string | null => {
      if (!providerTranslator) {
        return null;
      }

      const translated = providerTranslator(key, params);
      return translated !== key ? translated : null;
    },
    [providerTranslator],
  );

  const t = useCallback(
    (key: string, params?: AnalyticsParams) =>
      translateWithProvider(key, params) ?? translateAnalytics(key, params),
    [translateWithProvider],
  );

  const fallback = useCallback(
    (key: string, fallbackCopy: string, params?: AnalyticsParams) => {
      const translated = t(key, params);
      return translated === key ? fallbackCopy : translated;
    },
    [t],
  );

  const plural = useCallback(
    (key: string, count: number, params?: AnalyticsParams) => {
      const translatedPlural = translateWithProvider(
        buildPluralKey(key, count),
        {
          count,
          ...params,
        },
      );

      if (translatedPlural) {
        return translatedPlural;
      }

      return pluralizeAnalytics(key, count, params);
    },
    [translateWithProvider],
  );

  return {
    t,
    fallback,
    plural,
    deviceLabel: (device) => getAnalyticsDeviceLabel(device, t),
    formatTime: (value, locale) => formatAnalyticsTime(value, locale),
  };
}
